import { preview } from './format';
import type { FallbackPart, RunPart, ToolPart } from './run-reducer';

/** What every tool returns to the model (mirrors `ToolOutputEnvelope` in @af/agent-core). */
export interface ParsedEnvelope {
  content: string;
  trusted: boolean;
  source: string;
  isError: boolean;
  truncated: boolean;
}

const isEnvelope = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && (v as { type?: unknown }).type === 'tool_output';

function unescapeJsonString(s: string): string {
  try {
    return JSON.parse(`"${s}"`) as string;
  } catch {
    return s.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
}

/**
 * Unwrap the `{type:'tool_output', content}` envelope. Live events carry it as a JSON string that the server
 * may have cut at 2000 chars, so an unparsable string falls back to pulling the `content` prefix out by hand.
 */
export function parseEnvelope(output: unknown): ParsedEnvelope | null {
  let obj: unknown = output;
  if (typeof output === 'string') {
    const t = output.trimStart();
    if (!t.startsWith('{')) return null;
    try {
      obj = JSON.parse(t);
    } catch {
      if (!/^\{\s*"type"\s*:\s*"tool_output"/.test(t)) return null;
      const m = /"content"\s*:\s*"((?:[^"\\]|\\.)*)/.exec(t);
      return {
        content: m ? unescapeJsonString(m[1]!.replace(/\\$/, '')) : '',
        trusted: /"trusted"\s*:\s*true/.test(t),
        source: /"source"\s*:\s*"([^"]*)"/.exec(t)?.[1] ?? '',
        isError: /"isError"\s*:\s*true/.test(t),
        truncated: true,
      };
    }
  }
  if (!isEnvelope(obj)) return null;
  return {
    content: typeof obj.content === 'string' ? obj.content : JSON.stringify(obj.content ?? ''),
    trusted: obj.trusted === true,
    source: typeof obj.source === 'string' ? obj.source : '',
    isError: obj.isError === true,
    truncated: obj.truncated === true,
  };
}

export type PrettyContent = { kind: 'json'; value: unknown; text: string } | { kind: 'text'; text: string };

/** Pretty-print JSON content, leave everything else (usually markdown) as text. */
export function prettyContent(content: string): PrettyContent {
  const t = content.trim();
  if (t.startsWith('{') || t.startsWith('[')) {
    try {
      const value = JSON.parse(t);
      return { kind: 'json', value, text: JSON.stringify(value, null, 2) };
    } catch {
      /* not JSON (or truncated) */
    }
  }
  return { kind: 'text', text: content };
}

/** The readable text of a tool output (envelope unwrapped), or the stringified value. */
export function outputPlain(output: unknown): string {
  if (output == null) return '';
  const env = parseEnvelope(output);
  if (env) return env.content;
  if (typeof output === 'string') return output;
  try {
    return JSON.stringify(output, null, 2);
  } catch {
    return String(output);
  }
}

export type ToolActivityKind = 'messages' | 'tasks' | 'system';

/** Classify canonical tool metadata, never a friendly label or message body. */
export function toolActivityKind(name: string): ToolActivityKind {
  if (name === 'send_message_to_agent' || name === 'channel_post') return 'messages';
  if (name === 'delegate_task' || name === 'spawn_subagent') return 'tasks';
  return 'system';
}

const SPECIAL: Record<string, string> = {
  report_result: 'Reported the result',
  memory_search: 'Searched memory',
  memory_write: 'Saved to memory',
  delegate_task: 'Delegated a task',
  send_message_to_agent: 'Sent a direct message',
  channel_post: 'Posted in a channel',
  channel_read: 'Read a channel',
  channel_find: 'Looked for a channel',
  channel_create: 'Opened a channel',
  channel_invite: 'Invited colleagues to a channel',
  channel_join: 'Joined a channel',
  channel_search: 'Searched the channels',
  ask_human: 'Asked a human',
  load_skill: 'Loaded a skill',
  company_list_agents: 'Looked at the company',
  company_get_agent: 'Read an agent’s instructions',
  company_create_agent: 'Created an agent',
  company_update_agent: 'Changed an agent',
  company_archive_agent: 'Archived an agent',
  sandbox_exec: 'Ran code in the sandbox',
  sandbox_edit: 'Edited a file',
  sandbox_grep: 'Searched the code',
  sandbox_glob: 'Looked for files',
  repo_checkout: 'Checked out a repository',
  repo_diff: 'Reviewed the changes',
  repo_commit: 'Committed changes',
  repo_sync: 'Synced with the base branch',
  repo_save: 'Saved unfinished work to its branch',
  repo_publish: 'Opened a pull request',
  repo_checks: 'Checked the CI result',
  repo_push: 'Pushed to a branch',
  attachment_list: 'Looked at the attached files',
  attachment_get: 'Placed an attached file in the sandbox',
  trello_attach_file: 'Attached a file to a Trello card',
  web_search: 'Searched the web',
  web_fetch: 'Read a web page',
};

const words = (s: string) =>
  s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
const sentence = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

export interface FriendlyTool {
  /** e.g. "Trello" for MCP tools, null for built-ins. */
  source: string | null;
  /** e.g. "Read card". */
  label: string;
}

/** `mcp.trello.trelloReadCard` → {source:'Trello', label:'Read card'}; built-ins get a plain sentence. */
export function friendlyTool(name: string): FriendlyTool {
  if (SPECIAL[name]) return { source: null, label: SPECIAL[name] };
  const m = /^mcp\.([^.]+)\.(.+)$/.exec(name);
  if (m) {
    const server = m[1]!;
    let action = words(m[2]!);
    const prefix = words(server);
    if (action.startsWith(`${prefix} `)) action = action.slice(prefix.length + 1);
    return { source: sentence(prefix), label: sentence(action) || sentence(prefix) };
  }
  return { source: null, label: sentence(words(name)) };
}

/** Short one-line summary of a tool's arguments. */
export function argsSummary(input: unknown, max = 90): string {
  if (input == null) return '';
  if (typeof input === 'object' && !Array.isArray(input)) {
    const entries = Object.entries(input as Record<string, unknown>);
    if (entries.length === 1) return preview(entries[0]![1], max);
    return entries.map(([k, v]) => `${k}: ${preview(v, 40)}`).join(', ');
  }
  return preview(input, max);
}

export interface FinalAnswer {
  text: string;
  artifacts: { name: string; content?: string; url?: string }[];
}

const isReport = (p: RunPart): p is ToolPart =>
  p.kind === 'tool' && p.toolName === 'report_result' && p.state === 'done';

/** The answer a human should read: the `report_result` summary, else the last assistant text. */
export function finalAnswer(parts: RunPart[]): FinalAnswer | null {
  const rep = [...parts].reverse().find(isReport);
  const inp = rep?.input as { summary?: unknown; artifacts?: unknown } | undefined;
  if (typeof inp?.summary === 'string' && inp.summary.trim()) {
    const artifacts = Array.isArray(inp.artifacts)
      ? inp.artifacts.flatMap((a) => {
          if (!a || typeof a !== 'object' || typeof a.name !== 'string') return [];
          return [
            {
              name: a.name,
              ...(typeof a.content === 'string' ? { content: a.content } : {}),
              ...(typeof a.url === 'string' ? { url: a.url } : {}),
            },
          ];
        })
      : [];
    return { text: inp.summary, artifacts };
  }
  // Without report_result the answer is the text after the last tool call (earlier text is narration).
  const lastTool = parts.map((p) => p.kind).lastIndexOf('tool');
  const tail = parts.slice(lastTool + 1).filter((p) => p.kind === 'text' && p.text.trim());
  const last = tail[tail.length - 1];
  return last && last.kind === 'text' ? { text: last.text, artifacts: [] } : null;
}

/** Parts that belong in the collapsed "steps" list (everything except the answer itself). */
export function stepParts(parts: RunPart[], answer: FinalAnswer | null): RunPart[] {
  const answerPart = answer && !parts.some(isReport) ? lastAnswerPart(parts) : undefined;
  return parts.filter((p) => {
    if (isReport(p) || p === answerPart) return false;
    if (p.kind === 'text') return !!p.text.trim();
    return p.kind !== 'log' && p.kind !== 'fallback';
  });
}

function lastAnswerPart(parts: RunPart[]): RunPart | undefined {
  const lastTool = parts.map((p) => p.kind).lastIndexOf('tool');
  return parts
    .slice(lastTool + 1)
    .reverse()
    .find((p) => p.kind === 'text' && p.text.trim());
}

export type MessageSegment =
  | { kind: 'text'; part: Extract<RunPart, { kind: 'text' }> }
  | { kind: 'fallback'; part: FallbackPart }
  | { kind: 'steps'; parts: RunPart[] };

/**
 * A run in the order it happened: narration text stays where it was said, consecutive tool calls/sub-agents
 * form one collapsed steps group. (Folding earlier text into the steps made half a message vanish and the
 * chat jump the moment a tool was called.) The answer itself is rendered separately, after the segments.
 */
export function messageSegments(parts: RunPart[], answer: FinalAnswer | null): MessageSegment[] {
  const answerPart = answer && !parts.some(isReport) ? lastAnswerPart(parts) : undefined;
  const out: MessageSegment[] = [];
  for (const p of parts) {
    if (isReport(p) || p === answerPart || p.kind === 'log') continue;
    if (p.kind === 'text') {
      if (p.text.trim()) out.push({ kind: 'text', part: p });
      continue;
    }
    // A model switch is shown as its own line where it happened, never folded into the collapsed steps.
    if (p.kind === 'fallback') {
      out.push({ kind: 'fallback', part: p });
      continue;
    }
    const last = out[out.length - 1];
    if (last?.kind === 'steps') last.parts.push(p);
    else out.push({ kind: 'steps', parts: [p] });
  }
  return out;
}

export function stepCount(parts: RunPart[]): number {
  return parts.filter((p) => p.kind === 'tool' || p.kind === 'subagent').length;
}
